// inventoZen-Back-End/front/src/components/form-fields/TableFileField.tsx
import { PagingParameters } from "@/api/dtos";
import { getDocShareLink, getFileByDocId, useDeleteFile } from "@/api/file";
import { useGetForfait } from "@/api/forfait";
import EditFileModal from "@/components/modal/EditFileModal.tsx";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { toast } from "@/hooks/use-toast";
import { Forfait } from "@/lib/types";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import {
  CheckCircleIcon,
  CloudDownload,
  Crown,
  Link,
  MessageSquareWarning,
  Pencil,
  Trash2,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ShowOptionsDialog from "../hd/ShowOptionsDialog";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Dialog, DialogClose, DialogContent } from "../ui/dialog";
import { Progress } from "../ui/progress";
import { Separator } from "../ui/separator";
import { FormFieldProps } from "./types";

interface TableFileFieldProps extends Pick<FormFieldProps, "config"> {
  docId: string;
  refreshFiles?: boolean;
  setRefreshFiles?: (refresh: boolean) => void;
}

//TO DO: Find a way to use this component for other fields than file
const TableFileField: React.FC<TableFileFieldProps> = ({
  docId,
  config,
  refreshFiles,
  setRefreshFiles,
}) => {
  const [files, setFiles] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [totalSize, setTotalSize] = useState<number>(0);
  const { mutate: deleteFile } = useDeleteFile();
  const actions = config.table?.actions;
  const { data: forfait, refetch: refetchForfait } = useGetForfait();
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedOptionName, setSelectedOptionName] = useState<string>("");
  const [totalWeight, setTotalWeight] = useState<number>(0);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const documentToDelete = useRef<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [fileToEdit, setFileToEdit] = useState<any>(null);
  function subscribeToOptionSuccess(name: string) {
    setSelectedOptionName(name);
    setOpenConfirmation(true);
  }
  const openEditModal = (fileData: any) => {
    setFileToEdit(fileData);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setFileToEdit(null);
    fetchFilesData();
  };
  const downloadFile = async (row: any) => {
    try {
      // The file listing only exposes derived URLs (webp for images, `original`
      // for other types), so we resolve the raw original via the share endpoint
      // to download the real file whatever its format.
      const { data: fileUrl } = await getDocShareLink(row.id);
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const extension = row.mimetype ? `.${row.mimetype}` : "";
      const fileName =
        extension && !row.title?.toLowerCase().endsWith(extension.toLowerCase())
          ? `${row.title}${extension}`
          : row.title;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast({
        variant: "destructive",
        title: t("errorDownloadFile"),
      });
    }
  };

  const shareFile = async (row: any) => {
    try {
      const { data } = await getDocShareLink(row.id);
      navigator.clipboard.writeText(data);
      toast({
        variant: "success",
        title: t("copySuccess"),
      });
    } catch {
      toast({
        variant: "destructive",
        title: t("errorShareFile"),
      });
    }
  };

  useEffect(() => {
    let weight = forfait?.maxWeight ?? 0;
    if (forfait?.subscribedOptions?.length ?? 0 > 0) {
      const option = forfait?.subscribedOptions.find(
        (option) => option.type === "HD",
      );
      if (option) {
        weight += option.maxWeight ?? 0;
      }
    }
    setTotalWeight(weight);
  }, [forfait]);

  useEffect(() => {
    if (refreshFiles) {
      refetchForfait();
      setRefreshFiles?.(false);
      // Le fichier qui vient d'être ajouté n'est pas forcément dans la page
      // courante : on revient page 1, et c'est le useEffect [pagination] qui
      // refait le fetch (sinon on ferait deux appels pour rien).
      if (pagination.page !== 0) {
        setPagination((prev) => ({ ...prev, page: 0 }));
      } else {
        fetchFilesData();
      }
    }
  }, [refreshFiles]);

  const getSpaceSpace = () => {
    if (forfait?.usedSpaceInGB && totalWeight) {
      return (forfait?.usedSpaceInGB / totalWeight) * 100 > 100
        ? 100
        : (forfait?.usedSpaceInGB / totalWeight) * 100;
    }
    return 0;
  };

  const handleDeleteFile = async (id: string) => {
    await deleteFile(id);
    setTimeout(async () => {
      await fetchFilesData();
    }, 1000);
  };

  const { t } = useTranslation();

  const fetchFilesData = async (currentSorting?: SortingState) => {
    const sortingToUse = currentSorting || sorting;
    const data = await getFileByDocId(
      docId,
      sortingToUse.length > 0
        ? {
            orderBy: sortingToUse[0].id,
            orderDirection: sortingToUse[0].desc ? "desc" : "asc",
          }
        : {},
      pagination,
    );
    setFiles(data?.data ?? []);
    setTotalSize(data?.totalSize ?? 0);
  };

  const onSortingChange = useCallback(async (sorting: SortingState) => {
    setSorting(sorting);
    fetchFilesData(sorting);
  }, []);

  useEffect(() => {
    fetchFilesData();
  }, [pagination]);

  const columnsData = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      ...(config.table?.columns.map((column) => {
        if (column.id === "createdAt") {
          return {
            ...column,
            enableSorting: true,
            cell: ({ row }: any) => {
              const date = new Date(row.original.createdAt);
              return date.toLocaleDateString();
            },
          };
        }
        return column;
      }) ?? []),
      createActionsColumnDef(({ row }: any) => {
        const rowActions: TableRowAction[] = [];

        for (const action of actions ?? []) {
          switch (action.name) {
            case "edit":
              rowActions.push({
                id: "edit",
                label: t(ACTION_I18N_KEYS.edit),
                icon: Pencil,
                onSelect: () => openEditModal(row.original),
              });
              break;
            case "download":
              rowActions.push({
                id: "download",
                label: t("table.files.download"),
                icon: CloudDownload,
                onSelect: () => downloadFile(row.original),
              });
              break;
            case "share":
              rowActions.push({
                id: "share",
                label: t("shareLinkTooltip"),
                icon: Link,
                onSelect: () => shareFile(row.original),
              });
              break;
            case "delete":
              rowActions.push({
                id: "delete",
                label: t(ACTION_I18N_KEYS.delete),
                icon: Trash2,
                destructive: true,
                onSelect: () => {
                  setOpenDeleteDialog(true);
                  documentToDelete.current = row.original;
                },
              });
              break;
          }
        }

        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            <TableRowActionsMenu actions={rowActions} />
          </div>
        );
      }),
    ],
    [config, actions, t],
  );

  return (
    <>
      <DataTable
        className={config.className}
        columns={columnsData}
        data={files}
        options={{}}
        pagination={{
          pagination: pagination,
          totalSize: totalSize,
          setPage: (page) => {
            setPagination(page);
          },
        }}
        onSortingChange={onSortingChange}
        allowInternalSorting={true}
      />
      <div className="flex justify-end">
        <div className="flex flex-col gap-4 justify-center border border-gray-300 rounded-xl p-4 max-h-fit">
          <div className="flex font-bold text-center justify-center mt-6">
            {t("remainingStorageSpace")}
          </div>
          <Progress
            value={getSpaceSpace()}
            indicatorClassName={`${getSpaceSpace() < 70 ? "bg-primary" : getSpaceSpace() < 90 ? "bg-amber-500" : "bg-red-500"}`}
            className="w-full"
          />
          <div className="text-gray-600 text-[16px] font-[500] font-[Raleway] flex flex-row gap-2 items-center">
            {forfait?.usedSpaceInGB} Go /{" "}
            <p
              className={`${forfait?.subscribedOptions?.length === 0 ? "" : "text-hdOption-icon"}`}
            >
              {totalWeight} Go
            </p>
          </div>
          {getSpaceSpace() > 70 && (
            <div className="flex flex-col justify-center items-center">
              <MessageSquareWarning className="w-6 h-6 text-[#D18E3C] font-bold" />
              <div className="text-[#838383] text-center mb-2">
                {t("noMoreSpace")}
              </div>
            </div>
          )}
          <Separator
            orientation="vertical"
            className="w-full h-[0.5px] bg-gray-300"
          />
          {(forfait?.subscribedOptions?.length
            ? forfait?.subscribedOptions?.length > 0
            : false) &&
            getSpaceSpace() <= 70 && (
              <div className="flex flex-col gap-2">
                <div className="flex font-bold text-sm text-center justify-center mx-2 mt-2">
                  {t("suscribedOption")} :
                </div>
                <div className="flex flex-row gap-2 items-center text-hdOption-icon justify-center w-full font-bold text-xs">
                  <Crown className="w-5 h-5 mr-1" />
                  {forfait?.subscribedOptions
                    ?.filter((option) => option.type === "HD")
                    .map((option) => option.name)}
                </div>
              </div>
            )}
          <Button
            className="w-full bg-[#F1F5F7] text-primary font-bold hover:bg-primary/85 shadow-sm"
            onClick={() => navigate("/compte-principal")}
          >
            {t("manageStockageSpace")}
          </Button>
          <Button className="w-full" onClick={() => setShowDialog(true)}>
            <Crown className="w-4 h-4 mr-4" />
            {t("manageOptions")}
          </Button>
        </div>
      </div>
      <ShowOptionsDialog
        open={showDialog}
        onOpenChange={(open) => setShowDialog(open)}
        forfait={forfait ?? ({} as Forfait)}
        setSelectedOptionName={subscribeToOptionSuccess}
      />
      <Dialog open={openConfirmation} onOpenChange={setOpenConfirmation}>
        <DialogContent className="p-6">
          <div className="flex items-center justify-center my-4">
            <CheckCircleIcon className="w-8 h-8 text-primary" />
          </div>
          <div className="text-center text-2xl mx-4">
            {t("optionAjoutSuccesTitre1")}{" "}
            <strong>"{selectedOptionName}"</strong>{" "}
            {t("optionAjoutSuccesTitre2")}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
        <DialogContent className="p-6">
          <div className="flex items-center justify-center my-4">
            {t("fileSuppressionQuestion")}
          </div>
          <DialogClose>
            <div className="flex justify-between gap-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setOpenDeleteDialog(false)}
              >
                {t("formCancel")}
              </Button>
              <Button
                className="w-full"
                onClick={async () => {
                  setOpenDeleteDialog(false);
                  await handleDeleteFile(documentToDelete.current.id);
                  toast({
                    variant: "success",
                    description: t("toastFileSuppressionSuccesDescription"),
                  });
                }}
              >
                {t("confirmAddAttachmentDialog")}
              </Button>
            </div>
          </DialogClose>
        </DialogContent>
      </Dialog>
      <EditFileModal
        id={fileToEdit?.id}
        isOpen={isEditModalOpen}
        fileData={fileToEdit}
        onClose={closeEditModal}
      />
    </>
  );
};

export default TableFileField;
